/**
 * Muestreo de las señales de conexión de la videollamada del portal y su traducción a los tres
 * peldaños de aviso (propuesta «Avisos de conexión en la teleconsulta»).
 *
 * Aquí vive TODO lo que depende de LiveKit; la decisión de qué mostrar está en
 * `avisoConexion.ts`, que es lógica pura y sí tiene pruebas. La separación es a propósito: sin
 * ella, comprobar «un corte de 7 s no debe avisar» exigiría una videollamada real.
 *
 * Debe usarse DENTRO de `<LiveKitRoom>`.
 */
import { useEffect, useRef, useState } from 'react';
import { useConnectionState, useRemoteParticipants, useLocalParticipant } from '@livekit/components-react';
import { ConnectionState, ConnectionQuality, Track } from 'livekit-client';
import type { RemoteParticipant, RemoteAudioTrack, RemoteVideoTrack } from 'livekit-client';
import {
  crearDetectorConexion,
  elegirContraparte,
  type DetectorConexion,
  type EstadoAvisoConexion,
  type SenalConexion,
} from './avisoConexion';
import { logClient } from '../services/obsClientLogger';

/** Cada cuánto se leen las estadísticas de recepción. Dos segundos es el propio ritmo de
 *  monitorización de LiveKit; bajar de ahí no da más información y sí más trabajo. */
const MUESTREO_MS = 2_000;

const SIN_AVISO: EstadoAvisoConexion = { nivel: 'ninguno', senal: null, duracionMs: 0 };

/** Contadores de la última muestra, para saber si avanzaron. */
interface Contadores {
  fotogramas?: number;
  bytesAudio?: number;
}

/**
 * ¿Publica el otro ese medio y aquí no llega nada? Es «publicado pero no me llega»: la
 * publicación existe y no está muteada —así que no es que lo haya apagado— pero no tenemos
 * pista suscrita. Distinto de que se quede congelado, y distinto de que no publique.
 *
 * Esta comprobación faltaba. La primera versión del detector (03-09-2026) trataba este caso
 * como «no puedo opinar» y se quedaba callada, que es justo lo que pasó el 04-09: el paciente
 * no veía al médico, el médico sí había publicado su cámara, y no se registró un solo aviso.
 */
function medioAusente(pub: { isSubscribed?: boolean; isMuted?: boolean; track?: unknown } | undefined): boolean {
  if (!pub) return false;            // no publica: eso no es una falla de transporte
  if (pub.isMuted) return false;     // lo apagó a propósito
  return !pub.isSubscribed || !pub.track;
}

/**
 * ¿El video que llega se quedó congelado? Se compara `framesDecoded` con la muestra anterior:
 * si la pista está suscrita, sin silenciar, y los fotogramas no avanzaron, la imagen está
 * pegada aunque para el resto del sistema todo parezca correcto.
 *
 * Devuelve `undefined` cuando no se puede opinar (no hay pista, está silenciada o es la
 * primera muestra): eso NO es una señal mala, es ausencia de dato — la ausencia de pista la
 * cubre `medioAusente`.
 */
async function videoCongelado(p: RemoteParticipant, previo: Contadores): Promise<boolean | undefined> {
  const pub = p.getTrackPublication(Track.Source.Camera);
  if (!pub?.isSubscribed || pub.isMuted || !pub.videoTrack) {
    delete previo.fotogramas;
    return undefined;
  }
  const pista = pub.videoTrack as RemoteVideoTrack;
  const stats = await pista.getReceiverStats().catch(() => undefined);
  if (!stats) return undefined;
  const anterior = previo.fotogramas;
  previo.fotogramas = stats.framesDecoded;
  if (anterior === undefined) return undefined;
  return stats.framesDecoded <= anterior;
}

/** Igual que el anterior pero con el audio: la pista está viva y no llegan bytes. */
async function audioMudo(p: RemoteParticipant, previo: Contadores): Promise<boolean | undefined> {
  const pub = p.getTrackPublication(Track.Source.Microphone);
  if (!pub?.isSubscribed || pub.isMuted || !pub.audioTrack) {
    delete previo.bytesAudio;
    return undefined;
  }
  const pista = pub.audioTrack as RemoteAudioTrack;
  const stats = await pista.getReceiverStats().catch(() => undefined);
  if (!stats || stats.bytesReceived === undefined) return undefined;
  const anterior = previo.bytesAudio;
  previo.bytesAudio = stats.bytesReceived;
  if (anterior === undefined) return undefined;
  return stats.bytesReceived <= anterior;
}

export interface OpcionesAvisoConexion {
  /** Id de la consulta, para que el aviso quede registrado y se pueda medir después. */
  consultaId?: string;
  /** La cámara o el micrófono propios se soltaron (lo detecta `MediaSync`). */
  medioPropioCaido?: boolean;
  /** Quién mira: cambia solo el texto del registro, no el comportamiento. */
  componente?: string;
}

/**
 * Devuelve qué aviso corresponde mostrar ahora mismo. Registra en observabilidad cada vez que
 * el nivel cambia —incluida la recuperación— para poder medir si la mejora sirvió: cuántas
 * consultas se recuperaron solas y cuántas llegaron a necesitar instrucción.
 */
export function useAvisoConexion(opciones: OpcionesAvisoConexion = {}): EstadoAvisoConexion {
  const { consultaId, medioPropioCaido = false, componente = 'VideoCallPageV2' } = opciones;
  const estadoConexion = useConnectionState();
  const remotos = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  const [aviso, setAviso] = useState<EstadoAvisoConexion>(SIN_AVISO);

  // Refs para que el intervalo lea siempre lo último sin reprogramarse en cada render. Se
  // sincronizan en un efecto (no durante el render) porque escribir refs mientras React está
  // renderizando no es seguro con las renderizaciones interrumpibles.
  const detectorRef = useRef<DetectorConexion | null>(null);
  const contadoresRef = useRef<Contadores>({});
  const medioPropioRef = useRef(medioPropioCaido);
  const remotosRef = useRef(remotos);
  const estadoRef = useRef(estadoConexion);
  const calidadRef = useRef<ConnectionQuality | undefined>(undefined);
  const nivelPrevioRef = useRef<EstadoAvisoConexion['nivel']>('ninguno');

  useEffect(() => {
    medioPropioRef.current = medioPropioCaido;
    remotosRef.current = remotos;
    estadoRef.current = estadoConexion;
    calidadRef.current = localParticipant?.connectionQuality;
  });
  /** Duración del problema en la muestra anterior: al resolverse ya vale 0 y hay que decir
   *  cuánto llegó a durar. */
  const duracionPreviaRef = useRef(0);

  // El detector se crea cuando la llamada queda conectada: su gracia de arranque cuenta desde
  // ahí. Si la conexión se pierde del todo y vuelve, se conserva —no es una llamada nueva—.
  useEffect(() => {
    if (estadoConexion === ConnectionState.Connected && !detectorRef.current) {
      detectorRef.current = crearDetectorConexion(Date.now());
    }
  }, [estadoConexion]);

  useEffect(() => {
    let vivo = true;

    const muestrear = async () => {
      const detector = detectorRef.current;
      if (!detector) return;

      const malas: SenalConexion[] = [];
      const estado = estadoRef.current;
      if (estado === ConnectionState.Reconnecting || estado === ConnectionState.Disconnected) {
        malas.push('corte');
      }
      if (medioPropioRef.current) malas.push('medioPropioCaido');
      const calidad = calidadRef.current;
      if (calidad === ConnectionQuality.Poor || calidad === ConnectionQuality.Lost) {
        malas.push('calidadMala');
      }

      /* ¿Sigue llegando el audio de la otra persona? Confirmación POSITIVA, no ausencia de
         queja: solo con esto se puede degradar a «solo audio» en vez de alarmar. `undefined`
         = no se pudo comprobar, y entonces no se degrada. */
      let audioRecibiendose: boolean | undefined;

      // Solo se examinan las pistas cuando el canal está en pie: durante un corte, que no
      // avancen los fotogramas es la consecuencia, no una señal aparte.
      if (estado === ConnectionState.Connected) {
        // El médico mira al PACIENTE. En la sala también están el agente de transcripción y
        // los participantes de grabación: tomar «el primero» era una moneda al aire.
        const otro = elegirContraparte(remotosRef.current, 'paciente_');
        if (otro) {
          const camPub = otro.getTrackPublication(Track.Source.Camera);
          const micPub = otro.getTrackPublication(Track.Source.Microphone);
          // Sin ninguna publicación no hay nada que declarar ausente, pero tampoco es normal:
          // quien está en una llamada siempre publica audio.
          if (!camPub && !micPub) malas.push('contraparteMuda');
          if (medioAusente(camPub)) malas.push('videoAusente');
          if (medioAusente(micPub)) malas.push('audioAusente');
          const [congelado, mudo] = await Promise.all([
            videoCongelado(otro, contadoresRef.current),
            audioMudo(otro, contadoresRef.current),
          ]);
          if (congelado) malas.push('videoCongelado');
          if (mudo) malas.push('audioMudo');
          // `mudo === false` es la comprobación de que los bytes de audio SÍ avanzan.
          audioRecibiendose = mudo === undefined ? undefined : !mudo;
        } else {
          contadoresRef.current = {};
        }
      } else {
        contadoresRef.current = {};
      }

      if (!vivo) return;
      const siguiente = detector.evaluar(malas, Date.now(), { audioRecibiendose });
      setAviso((previo) =>
        previo.nivel === siguiente.nivel && previo.senal === siguiente.senal ? previo : siguiente,
      );

      if (siguiente.nivel !== nivelPrevioRef.current) {
        const antes = nivelPrevioRef.current;
        nivelPrevioRef.current = siguiente.nivel;
        logClient({
          nivel: siguiente.nivel === 'grave' ? 'WARN' : 'INFO',
          categoria: 'LLAMADA',
          codigo: siguiente.nivel === 'ninguno' ? 'AVISO_CONEXION_RESUELTO'
            : siguiente.nivel === 'leve' ? 'AVISO_CONEXION_LEVE'
            : siguiente.nivel === 'soloAudio' ? 'AVISO_CONEXION_SOLO_AUDIO'
            : 'AVISO_CONEXION_GRAVE',
          componente,
          consultaId,
          resultado: siguiente.nivel === 'ninguno' ? 'ok' : 'error',
          mensaje: siguiente.nivel === 'ninguno'
            ? `La conexión se normalizó tras ${Math.round(duracionPreviaRef.current / 1000)} s`
            : `Aviso de conexión ${siguiente.nivel} por ${siguiente.senal} `
              + `(${Math.round(siguiente.duracionMs / 1000)} s, venía de ${antes})`,
        });
      }
      duracionPreviaRef.current = siguiente.duracionMs;
    };

    const id = setInterval(() => { void muestrear(); }, MUESTREO_MS);
    return () => { vivo = false; clearInterval(id); };
  }, [consultaId, componente]);

  return aviso;
}
